import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import css from './SaaSAdminCredentialsPage.css';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';
import { IconSpinner } from '../../components';
import {
  fetchAdminCredentials,
  adminApproveCredential,
  adminRejectCredential,
} from '../../custom-ducks/credentials.duck';

const FILTERS = [
  { id: 'pending', label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

function SaaSAdminCredentialsPage({
  currentUser,
  onFetch,
  onApprove,
  onReject,
  history,
}) {
  const [filter, setFilter] = useState('pending');
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [actingOnId, setActingOnId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectingCoachId, setRejectingCoachId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = (f = filter) => {
    setLoading(true);
    setFetchError(null);
    onFetch(f, 100)
      .then((res) => {
        const data = (res && res.data && res.data.credentials) || [];
        setCredentials(data);
        setLoading(false);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          setForbidden(true);
          setLoading(false);
        } else {
          setFetchError({
            reason: err?.response?.data?.error || 'unknown',
            detail: err?.response?.data?.detail || null,
            adminFixUrl: err?.response?.data?.adminFixUrl || null,
          });
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const handleApprove = (c) => {
    if (!window.confirm(`Approve "${c.title}" for ${c.coachName}?`)) return;
    setActingOnId(c.id);
    onApprove(c.coachId, c.id)
      .then(() => {
        setActingOnId(null);
        load();
      })
      .catch(() => setActingOnId(null));
  };

  const openReject = (c) => {
    setRejectingId(c.id);
    setRejectingCoachId(c.coachId);
    setRejectReason('');
  };

  const submitReject = () => {
    if (!rejectingId) return;
    if (rejectReason.trim().length < 5) {
      window.alert('Please provide a brief reason (at least 5 characters). It will be emailed to the coach.');
      return;
    }
    setActingOnId(rejectingId);
    onReject(rejectingCoachId, rejectingId, rejectReason.trim())
      .then(() => {
        setActingOnId(null);
        setRejectingId(null);
        setRejectingCoachId(null);
        setRejectReason('');
        load();
      })
      .catch(() => setActingOnId(null));
  };

  if (forbidden) {
    return (
      <PageWrapper>
        <MainDashboardWrapper title="Admin — Credentials" page="admin-credentials">
          <div className={css.base}>
            <div className={css.forbiddenCard}>
              <h1>Not authorised</h1>
              <p>This page is restricted to Viveka admins.</p>
              <button type="button" onClick={() => history.push('/v-coach/dashboard')}>
                Back to dashboard
              </button>
            </div>
          </div>
        </MainDashboardWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <MainDashboardWrapper title="Admin — Credentials" page="admin-credentials">
        <div className={css.base}>
          <div className={css.header}>
            <div>
              <h1 className={css.title}>Credential review queue</h1>
              <p className={css.subtitle}>
                Custom ("Other") credential submissions awaiting Viveka verification.
              </p>
            </div>
            <div className={css.filterRow}>
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`${css.filterButton} ${filter === f.id ? css.filterButton_active : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading && <IconSpinner />}

          {!loading && fetchError && (
            <div className={css.errorCard}>
              <p><strong>Couldn't load the queue.</strong></p>
              <p className={css.errorReason}>
                Error code: <code>{fetchError.reason}</code>
                {fetchError.adminFixUrl && (
                  <>
                    {' · '}
                    <a href={fetchError.adminFixUrl} target="_blank" rel="noopener noreferrer">
                      Create required index
                    </a>
                  </>
                )}
              </p>
              {fetchError.detail && <pre className={css.errorDetail}>{fetchError.detail}</pre>}
              <button type="button" onClick={() => load()}>Retry</button>
            </div>
          )}

          {!loading && !fetchError && credentials.length === 0 && (
            <p className={css.empty}>
              No credentials in this view. Nice — the queue is clear.
            </p>
          )}

          {!loading && !fetchError && credentials.length > 0 && (
            <ul className={css.list}>
              {credentials.map((c) => (
                <li key={c.id} className={css.row}>
                  <div className={css.rowMain}>
                    <div className={css.rowTopLine}>
                      <span className={css.coachName}>{c.coachName}</span>
                      <span className={`${css.statusPill} ${css['statusPill_' + c.status]}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className={css.rowTitle}>{c.title}</div>
                    <div className={css.rowMeta}>
                      {c.issuingOrg}
                      {c.issuedYear && ` · ${c.issuedYear}`}
                      {' · '}
                      Submitted {formatDate(c.submittedAt)}
                    </div>
                    {c.rejectionReason && (
                      <div className={css.rejectionNote}>
                        <strong>Reason:</strong> {c.rejectionReason}
                      </div>
                    )}
                  </div>
                  <div className={css.rowActions}>
                    {c.fileUrl && (
                      <a
                        href={c.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.viewFileButton}
                      >
                        View file
                      </a>
                    )}
                    {c.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          className={css.approveButton}
                          disabled={actingOnId === c.id}
                          onClick={() => handleApprove(c)}
                        >
                          {actingOnId === c.id ? '…' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className={css.rejectButton}
                          disabled={actingOnId === c.id}
                          onClick={() => openReject(c)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Reject reason modal — keep simple, no Popup library needed */}
          {rejectingId && (
            <div className={css.modalOverlay} onClick={() => setRejectingId(null)}>
              <div className={css.modal} onClick={(e) => e.stopPropagation()}>
                <h2>Reject credential</h2>
                <p>
                  The coach will receive an email with this reason and a link to resubmit.
                  Be clear and dignified — they may be embarrassed.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  placeholder="e.g. We couldn't verify this credential with the issuing organisation. If you've moved schools or the certificate was issued under a different name, please resubmit with supporting documentation."
                  maxLength={500}
                />
                <div className={css.modalActions}>
                  <button type="button" onClick={() => setRejectingId(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={css.rejectButton}
                    disabled={actingOnId === rejectingId || rejectReason.trim().length < 5}
                    onClick={submitReject}
                  >
                    {actingOnId === rejectingId ? 'Sending…' : 'Reject + email coach'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </MainDashboardWrapper>
    </PageWrapper>
  );
}

const mapStateToProps = (state) => {
  const { currentUser } = state.user || {};
  return { currentUser };
};

const mapDispatchToProps = (dispatch) => ({
  onFetch: (filter, limit) => dispatch(fetchAdminCredentials(filter, limit)),
  onApprove: (coachId, credentialId) => dispatch(adminApproveCredential(coachId, credentialId)),
  onReject: (coachId, credentialId, reason) => dispatch(adminRejectCredential(coachId, credentialId, reason)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(SaaSAdminCredentialsPage);
